import React from 'react';
import {
  Table as MuiTable,
  TableRow as MuiTableRow,
  TableHead as MuiTableHead,
  TableBody as MuiTableBody,
  TableCell as MuiTableCell,
  Typography,
  SxProps,
} from '@mui/material';
import {TableLoading} from './table-loading';
import {TableContainer} from './table-container';
import {TableHeaderSortCell, TableHeaderSortCellProps} from './table-header-sort-cell';

export type TableSortDirection = 'asc' | 'desc';

type TableBaseProps = {
  children?: React.ReactNode;
  onClick?: (evt: React.MouseEvent) => void;
  sx?: SxProps;
  className?: string;
};

export type TableProps = TableBaseProps & {
  loading?: boolean;
  stickyHeader?: boolean;
};

/**
 * The main Table component.
 *
 * @example
 * ```
 * // Basic usage
 * <Table>
 *   <Table.Head>
 *     <Table.Row>
 *       <Table.Cell>Header 1</Table.Cell>
 *       <Table.Cell>Header 2</Table.Cell>
 *     </Table.Row>
 *   </Table.Head>
 *
 *   <Table.Body>
 *     <Table.Row>
 *       <Table.Cell>Row 1, Cell 1</Table.Cell>
 *       <Table.Cell>Row 1, Cell 2</Table.Cell>
 *     </Table.Row>
 *     <Table.Row>
 *       <Table.Cell>Row 2, Cell 1</Table.Cell>
 *       <Table.Cell>Row 2, Cell 2</Table.Cell>
 *     </Table.Row>
 *   </Table.Body>
 * </Table>
 * ```
 *
 * @example
 *```
 * // Common use case
 * type TableColumnId = 'name' | 'description' | 'foo';
 * const [isLoading, setIsLoading] = useState(false);
 * const [sortDirection, setSortDirection] = useState<TableSortDirection>('desc');
 * const [sortBy, setSortBy] = useState<TableColumnId>('date');
 *
 * const handleColumnSort = (columnId: TableColumnId) => {
 *  const isAsc = sortBy === columnId && sortDirection === 'asc';
 *  setSortDirection(isAsc ? 'desc' : 'asc');
 *  setSortBy(columnId);
 * };
 *
 * const sortedItems = useMemo(() => {
 *  // handle sorting logic here
 * }, [props.items, sortBy, sortDirection]);
 *
 * return (
 *  <Table loading={isLoading}>
 *   <Table.CommonHeader<TableColumnId>
 *      sortBy={sortBy}
 *      sortDirection={sortDirection}
 *      onColumnSort={handleColumnSort}
 *    >
 *     <Table.HeaderSortCell
 *        id="name"
 *        label="Nome"
 *      />
 *      <Table.HeaderSortCell
 *        id="description"
 *        label="Descrição"
 *      />
 *     <Table.HeaderSortCell
 *       id="foo"
 *       label="Foo"
 *     />
 *   </Table.CommonHeader>
 *
 *   <Table.CommonBody loading={isLoading}>
 *      {sortedItems.map((item) => (
 *        <Table.Row key={item.id}>
 *          <Table.LabelCell>{item.name}</Table.Cell>
 *          <Table.LabelCell>{item.description}</Table.Cell>
 *          <Table.Cell>
 *            <Avatar>{item.foo}</Avatar>
 *          </Table.Cell>
 *        </Table.Row>
 *      ))}
 *    </Table.CommonBody>
 *  </Table>
 * )
 * ```
 */
export function Table(props: TableProps) {
  return (
    <TableContainer className="table" loading={props.loading}>
      <MuiTable stickyHeader={props.stickyHeader}>{props.children}</MuiTable>
    </TableContainer>
  );
}

/** Wrapper for the MUI TableHead component. */
Table.Head = function TableHead({children, ...rest}: TableBaseProps) {
  return <MuiTableHead {...rest}>{children}</MuiTableHead>;
};

/** Wrapper for the MUI TableBody component. */
Table.Body = function TableBody({children, ...rest}: TableBaseProps) {
  return <MuiTableBody {...rest}>{children}</MuiTableBody>;
};

/** Wrapper for the MUI TableRow component. */
Table.Row = function TableRow({children, ...rest}: TableBaseProps) {
  return <MuiTableRow {...rest}>{children}</MuiTableRow>;
};

/** Wrapper for the MUI TableCell component. */
Table.Cell = function TableCell({children, ...rest}: TableBaseProps) {
  return <MuiTableCell {...rest}>{children}</MuiTableCell>;
};

/**
 * A MUI TableCell but with sorting capabilities.
 *
 * @example
 * ```
 * <>
 *  <Table.HeaderSortCell
 *    label="Nome"
 *    active={sortBy === 'name'}
 *    direction={sortDirection}
 *    onClick={() => handleColumnSort('name')}
 *  />
 *  <Table.HeaderSortCell
 *    label="Descrição"
 *    active={sortBy === 'description'}
 *    direction={sortDirection}
 *    onClick={() => handleColumnSort('description')}
 *    />
 * </>
 * ```
 */
Table.HeaderSortCell = TableHeaderSortCell;

/** A MUI TableCell that wraps its children in a Typography component. */
Table.LabelCell = function TableTypographyCell({children, ...rest}: TableBaseProps) {
  return (
    <MuiTableCell {...rest}>
      <Typography>{children}</Typography>
    </MuiTableCell>
  );
};

// --------------------------------------------------------------------------------------
// --------------------------------------------------------------------------------------
//      Common Header and Body components, with sorting & loading functionalities.
// --------------------------------------------------------------------------------------
// --------------------------------------------------------------------------------------

/**
 * Common Table Body component that can show a loading UI state.
 * To be used in most cases where you want to display a table body with loading state.
 */
Table.CommonBody = function TableCommonBody({
  children,
  loading,
  loadingSize,
  loadingGap,
  ...rest
}: TableBaseProps & {
  loading?: boolean;
  loadingSize?: number;
  loadingGap?: string;
}) {
  return (
    <MuiTableBody {...rest}>
      {loading ? <TableLoading size={loadingSize} gap={loadingGap} /> : children}
    </MuiTableBody>
  );
};

/**
 * Common Table Header component that can handle sorting.
 * To be used in most cases where you want to display a table header with sorting capabilities.
 *
 * @example
 * ````
 * <Table.CommonHeader
 *  sortBy="name"
 *  sortDirection="asc"
 *  onColumnSort={(sortBy) => console.log('Column sorted by:', sortBy)}
 * >
 *   <Table.HeaderSortCell id="name" label="Name" />
 *   <Table.HeaderSortCell id="description" label="Description" />
 *   <Table.HeaderSortCell id="date" label="Date" />
 * </Table.CommonHeader>
 * ````
 */
Table.CommonHeader = function TableCommonHeader<ColumnId extends string>({
  children,
  ...rest
}: TableBaseProps & {
  sortBy?: ColumnId;
  sortDirection?: TableSortDirection;
  onSortClick?: (sortBy: ColumnId) => void;
}) {
  return (
    <MuiTableHead {...rest}>
      <MuiTableRow>
        {React.Children.map(children, (child) => {
          // --- If the child is a TableHeaderSortCell, extend its props with sorting capabilities ---
          if (React.isValidElement(child) && child.type === TableHeaderSortCell) {
            // Extend the props
            const sortCellProps: TableHeaderSortCellProps = {
              ...child.props,
              direction: rest.sortDirection,
              active: rest.sortBy === child.props.id,
              onClick: () => rest.onSortClick?.(child.props.id as ColumnId),
            };

            // Clone with extended props
            return React.cloneElement(child, sortCellProps);
          }

          // --- If the child is not a TableHeaderSortCell, return it as is ---
          return child;
        })}
      </MuiTableRow>
    </MuiTableHead>
  );
};
